import {
  Avatar,
  Box,
  Chip,
  Divider,
  ListItem,
  ListItemAvatar,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConversationListItem } from '../types/messages.types';
import { formatConversationTimestamp } from '@/shared/utils/formatters';

interface SystemConversationItemProps {
  conversation: ConversationListItem;
  showDivider: boolean;
  onClick?: () => void;
}

export function SystemConversationItem({
  conversation,
  showDivider,
  onClick,
}: SystemConversationItemProps) {
  const { t, i18n } = useTranslation();

  const buyerName = conversation.buyerName ?? conversation.interlocutor.name;
  const offerTitle = conversation.offerTitle || t('messages.offerTitleFallback');
  const timestamp = conversation.lastMessageAt
    ? formatConversationTimestamp(
        conversation.lastMessageAt,
        i18n.language,
        t('messages.yesterday')
      )
    : '';

  return (
    <Box>
      <ListItem
        alignItems="flex-start"
        sx={{
          px: 3,
          py: 2,
          cursor: onClick ? 'pointer' : 'default',
          background: theme =>
            `linear-gradient(135deg, ${theme.palette.success.light}22 0%, ${theme.palette.success.light}44 100%)`,
          borderLeft: '4px solid',
          borderColor: 'success.main',
          '&:hover': onClick
            ? {
                background: theme =>
                  `linear-gradient(135deg, ${theme.palette.success.light}44 0%, ${theme.palette.success.light}66 100%)`,
              }
            : {},
        }}
        onClick={onClick}
        data-testid={`system-conversation-item-${conversation.id}`}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: 'success.main',
              width: 44,
              height: 44,
              fontSize: '1.4rem',
              boxShadow: theme => `0 0 0 3px ${theme.palette.success.light}`,
            }}
          >
            {'🎉'}
          </Avatar>
        </ListItemAvatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1 }}>
              {buyerName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {timestamp}
            </Typography>
            <Chip
              label={t('messages.systemConversation.badge')}
              size="small"
              color="success"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
            />
          </Stack>

          <Typography variant="body2" fontWeight={600} color="success.dark">
            {t('messages.systemConversation.body', { name: buyerName, offerTitle })}
          </Typography>

          <Typography variant="body2" fontWeight={500} color="success.dark" sx={{ mt: 0.25 }}>
            {t('messages.systemConversation.cta')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {conversation.buyerPhone
              ? t('messages.systemConversation.phoneLabel', { phone: conversation.buyerPhone })
              : ''}
          </Typography>
        </Box>
      </ListItem>
      {showDivider && <Divider component="li" />}
    </Box>
  );
}
